import React, { useEffect, useMemo, useState } from 'react'

import { TPayoutInfoDetails } from '@/app/(main)/rewards/select-withdraw-method/page'
import { Button, RadioPayoutInfo, AddPayoutInfo } from '@/components'
import { useGetClientConfigQuery } from '@/libs/apis/queries/use-get-client-config-query'
import { TWithdrawalMethodDetail } from '@/libs/apis/queries/use-get-withdrawal-method-query'
import bankList from '@/libs/constants/bank-list'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useGetParams from '@/libs/hooks/use-get-params'
import useToast from '@/libs/hooks/use-toast'
import parseWithdrawOption from '@/libs/logics/parse-withdraw-option'
import { EPayoutType } from '@/libs/types/global.types'

export type TWithdrawMethod = {
  readonly payoutOptions: TWithdrawalMethodDetail[]
  readonly phoneNumber: string
  readonly onClickAddBankAccount: () => void
  readonly onClickEWalletAccount: () => void
  readonly onClickWithdraw: (e: TPayoutInfoDetails) => void
  readonly reward: number
}

export default function WithdrawMethod({
  payoutOptions,
  phoneNumber,
  onClickAddBankAccount,
  onClickEWalletAccount,
  onClickWithdraw,
  reward,
}: TWithdrawMethod) {
  const [selectedWithdrawOption, setSelectedWithdrawOption] = useState<TPayoutInfoDetails | null>(
    null,
  )
  const { toast_message } = useGetParams()
  const toast = useToast()
  const handleSelectWithdrawOption = async (option: string) => {
    const parsedWithdrawOption = await parseWithdrawOption({
      payoutOptions: payoutOptions,
      selectedOption: option,
      phoneNumber: phoneNumber,
    })

    return setSelectedWithdrawOption(parsedWithdrawOption)
  }
  const { partnerIdNonIntegration, partnerId } = useAppSelector((state) => state.user)

  const hasPartnerId = !!partnerId || !!partnerIdNonIntegration

  const { data: dataClientConfig, isFetching: isFetchingClientConfig } = useGetClientConfigQuery({
    clientId: partnerIdNonIntegration || partnerId,
    queryOption: {
      enabled: hasPartnerId,
    },
  })

  useEffect(() => {
    if (payoutOptions && payoutOptions?.length > 0) {
      const defaultValue = payoutOptions.filter(
        (option) => option.isDefault && reward > option.minimalWithdrawAmount,
      )
      if (defaultValue?.length > 0) handleSelectWithdrawOption(defaultValue[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutOptions])

  const handleShowMessage = () => {
    if (toast_message) {
      toast.show({
        title: toast_message,
        description: '',
        type: 'success',
      })
    }
  }

  useEffect(() => {
    handleShowMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddPayoutInfo = (option: EPayoutType) => {
    if (option === EPayoutType?.BANK) {
      return onClickAddBankAccount()
    }

    if (option === EPayoutType?.EWALLET) {
      return onClickEWalletAccount()
    }
  }

  const accountInfo = useMemo(() => {
    const bankInfo = payoutOptions?.find((val) => val.type === EPayoutType.BANK)
    const ewalletInfo = payoutOptions?.filter((val) => val.type === EPayoutType.EWALLET)

    return {
      bankInfo,
      ewalletInfo,
    }
  }, [payoutOptions])

  const renderBankInfo = () => {
    const bankIsIncluded = dataClientConfig?.result?.withdrawMethodWhitelist?.find(
      (res) => res?.type === EPayoutType.BANK,
    )

    if (hasPartnerId && !isFetchingClientConfig && !bankIsIncluded) {
      return null
    }

    if (accountInfo?.bankInfo) {
      const isBelowEarningLimit = reward < accountInfo?.bankInfo?.minimalWithdrawAmount

      return (
        <>
          <div className='flex flex-col gap-1'>
            <h2 className='text-xs font-semibold text-pop-grey-600'>Bank Transfer</h2>
          </div>
          <RadioPayoutInfo
            value={accountInfo?.bankInfo.id}
            image={bankList?.[accountInfo?.bankInfo.provider]?.src ?? bankList.unknown.src}
            altImage={bankList?.[accountInfo?.bankInfo.provider]?.alt ?? bankList.unknown.alt}
            checked={
              accountInfo?.bankInfo?.enable
                ? selectedWithdrawOption?.id === accountInfo?.bankInfo.id
                : false
            }
            provider={bankList?.[accountInfo?.bankInfo.provider]?.name}
            accountNumber={accountInfo?.bankInfo.accountNumber}
            onClick={() => handleSelectWithdrawOption(accountInfo?.bankInfo?.id || '')}
            showWithdrawalLabel={!!accountInfo?.bankInfo.isAutoWithdrawal}
            isWithdrawalEnabled={accountInfo?.bankInfo.isAutoWithdrawal}
            isSpecialAccount={accountInfo?.bankInfo.isSpecial}
            adminFee={accountInfo?.bankInfo.adminFee}
            minimumWithdrawal={accountInfo?.bankInfo.minimalWithdrawAmount}
            disabled={reward < accountInfo?.bankInfo?.adminFee && !accountInfo?.bankInfo.isFree}
            isFree={accountInfo?.bankInfo.isFree}
            reward={reward}
            active={accountInfo?.bankInfo?.enable}
            isBelowEarningLimit={isBelowEarningLimit}
          />
        </>
      )
    }

    return (
      <>
        <div className='flex flex-col gap-1'>
          <h2 className='text-xs font-semibold text-pop-grey-600'>Bank Transfer</h2>
        </div>
        <AddPayoutInfo
          type={EPayoutType.BANK}
          onClick={() => handleAddPayoutInfo(EPayoutType.BANK)}
        />
      </>
    )
  }

  const renderEWallet = () => {
    if (accountInfo?.ewalletInfo && accountInfo.ewalletInfo.length > 0) {
      return (
        <>
          <div className='flex flex-col gap-1'>
            <h2 className='text-xs font-semibold text-pop-grey-600'>E-Wallet</h2>
            <p className='text-xs font-normal'>
              Make sure your phone number is linked to your E-Wallet.
            </p>
          </div>
          {accountInfo.ewalletInfo.map((value, index) => {
            const provider = value.provider.toLocaleLowerCase()
            const isBelowEarningLimit =
              provider !== 'gopay' && reward < value?.minimalWithdrawAmount

            return (
              <RadioPayoutInfo
                key={`${value}-${index}`}
                value={value.id}
                active={value?.enable}
                image={bankList?.[provider]?.src ?? bankList.unknown.src}
                altImage={bankList?.[value.provider]?.alt ?? bankList.unknown.alt}
                checked={value?.enable ? selectedWithdrawOption?.id === value.id : false}
                provider={bankList?.[provider]?.name}
                accountNumber={value?.accountNumber}
                onClick={() => handleSelectWithdrawOption(value.id)}
                showWithdrawalLabel={!!value.isAutoWithdrawal}
                isWithdrawalEnabled={value.isAutoWithdrawal}
                isSpecialAccount={value.isSpecial}
                adminFee={value.adminFee}
                minimumWithdrawal={value.minimalWithdrawAmount}
                disabled={reward < value?.adminFee && !value.isFree}
                isFree={value.isFree}
                reward={reward}
                isBelowEarningLimit={isBelowEarningLimit}
              />
            )
          })}
        </>
      )
    }
    return (
      <>
        <div className='flex flex-col gap-1'>
          <h2 className='text-xs font-semibold text-pop-grey-600'>E-Wallet</h2>
          <p className='text-xs font-normal'>
            Make sure your phone number is linked to your E-Wallet.
          </p>
        </div>
        <AddPayoutInfo
          type={EPayoutType.EWALLET}
          onClick={() => handleAddPayoutInfo(EPayoutType.EWALLET)}
        />
      </>
    )
  }

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col gap-4'>
        {renderBankInfo()}
        {renderEWallet()}
      </div>
      <Button
        type='submit'
        variant='contained'
        color='primary'
        disabled={!selectedWithdrawOption}
        disabledVarian='disabled'
        onClick={() => selectedWithdrawOption && onClickWithdraw(selectedWithdrawOption)}
        aria-label='Withdraw'
      >
        Withdraw
      </Button>
    </div>
  )
}
